/**
 * DENET — ürün sayfası
 * Galeri, yakınlaştırma, varyant seçimi, adet ve mobil satın alma çubuğu.
 * Varyant değişince fiyat/stok/URL güncellenir; sayfa yeniden yüklenmez.
 */

import { DenetElement, define, qsa, rafThrottle, formatMoney, emit, EVENTS } from '@denet/core';

class DnProduct extends DenetElement {
  mounted() {
    this.#initGallery();
    this.#initZoom();
    this.#initQuantity();
    this.#initVariants();
    this.#initBuyBar();
    this.#initAddToCart();
  }

  /* ---------------------------------------------------------------- galeri */
  #initGallery() {
    this.stageImage = /** @type {HTMLImageElement | null} */ (this.ref('stageImage'));
    this.thumbs = /** @type {HTMLElement[]} */ (qsa('[data-thumb]', this));
    this.index = Math.max(0, this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true'));
    if (!this.stageImage) return;

    for (const [i, thumb] of this.thumbs.entries()) {
      thumb.addEventListener('click', () => this.#show(i), { signal: this.signal });
    }

    /* Mobilde parmakla sağa-sola kaydırma. Dikey kaydırma sayfaya bırakılır
       (touch-action: pan-y); yatay hareket eşiği geçerse görsel değişir. */
    const stage = this.ref('stage');
    if (!stage || this.thumbs.length < 2) return;
    let x0 = 0; let y0 = 0; let izleniyor = false;
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      x0 = e.clientX; y0 = e.clientY; izleniyor = true;
    }, { signal: this.signal });
    stage.addEventListener('pointerup', (e) => {
      if (!izleniyor) return;
      izleniyor = false;
      const dx = e.clientX - x0; const dy = e.clientY - y0;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
      this.kaydirildi = true;                       // ardından gelen click büyütmeyi açmasın
      this.#show(this.index + (dx < 0 ? 1 : -1));
      window.setTimeout(() => { this.kaydirildi = false; }, 350);
    }, { signal: this.signal });
    stage.addEventListener('pointercancel', () => { izleniyor = false; }, { signal: this.signal });
  }

  /** Küçük görsellerden i. sıradakini sahneye alır (uçlarda başa/sona döner). */
  #show(i) {
    if (!this.stageImage || this.thumbs.length === 0) return;
    const n = this.thumbs.length;
    this.index = ((i % n) + n) % n;
    const thumb = this.thumbs[this.index];
    const src = thumb.dataset.src;
    if (!src) return;
    const stage = this.ref('stage');
    const image = this.stageImage;
    const degistir = () => {
      image.removeAttribute('srcset');
      image.setAttribute('src', src);
      stage?.classList.remove('is-degisiyor');
    };
    /* Kısa bir solma: yeni görsel yüklenince geri gelir, bekletmez. */
    stage?.classList.add('is-degisiyor');
    const on = new Image();
    on.onload = degistir; on.onerror = degistir;
    on.src = src;
    window.setTimeout(degistir, 400);
    for (const other of this.thumbs) other.setAttribute('aria-current', String(other === thumb));
    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (this.zoomDialog?.open) this.#zoomSync();
  }

  #initZoom() {
    const stage = this.ref('stage');
    const dialog = /** @type {HTMLDialogElement | null} */ (this.ref('zoom'));
    const zoomImage = this.ref('zoomImage');
    if (!stage || !dialog || !zoomImage || !this.stageImage) return;
    this.zoomDialog = dialog;

    stage.addEventListener('click', () => {
      if (this.kaydirildi) return;
      dialog.classList.remove('is-yakin');
      this.#zoomSync();
      dialog.showModal();
    }, { signal: this.signal });

    const tek = this.thumbs.length < 2;
    for (const ad of ['zoomPrev', 'zoomNext']) { const d = this.ref(ad); if (d) d.hidden = tek; }
    this.ref('zoomPrev')?.addEventListener('click', () => this.#show(this.index - 1), { signal: this.signal });
    this.ref('zoomNext')?.addEventListener('click', () => this.#show(this.index + 1), { signal: this.signal });
    this.ref('zoomClose')?.addEventListener('click', () => dialog.close(), { signal: this.signal });

    /* Görsele dokununca yakınlaşır; yakınken kaydırılarak gezilir. */
    const kaydir = this.ref('zoomScroll');
    kaydir?.addEventListener('click', (e) => {
      const yakin = dialog.classList.toggle('is-yakin');
      if (!yakin || !(kaydir instanceof HTMLElement)) return;
      const k = kaydir.getBoundingClientRect();
      requestAnimationFrame(() => {
        kaydir.scrollLeft = ((e.clientX - k.left) / k.width) * (kaydir.scrollWidth - k.width);
        kaydir.scrollTop = ((e.clientY - k.top) / k.height) * (kaydir.scrollHeight - k.height);
      });
    }, { signal: this.signal });

    dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); }, { signal: this.signal });
    dialog.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.#show(this.index - 1);
      if (e.key === 'ArrowRight') this.#show(this.index + 1);
    }, { signal: this.signal });
  }

  #zoomSync() {
    const zoomImage = this.ref('zoomImage');
    const src = this.thumbs[this.index]?.dataset.src || this.stageImage?.currentSrc || this.stageImage?.getAttribute('src') || '';
    zoomImage?.setAttribute('src', src);
    this.zoomDialog?.classList.remove('is-yakin');
    const sayac = this.ref('zoomCount');
    if (sayac) {
      sayac.hidden = this.thumbs.length < 2;
      sayac.textContent = `${this.index + 1} / ${this.thumbs.length}`;
    }
  }

  /* ----------------------------------------------------------------- adet */
  #initQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('.dn-qty__input'));
    if (!input) return;

    const sync = () => {
      const value = Math.max(1, Number(input.value) || 1);
      input.value = String(value);
      const minus = this.querySelector('[data-q="-1"]');
      if (minus instanceof HTMLButtonElement) minus.disabled = value <= 1;
    };

    for (const button of qsa('[data-q]', this)) {
      button.addEventListener('click', () => {
        input.value = String(Math.max(1, Number(input.value) + Number(/** @type {HTMLElement} */ (button).dataset.q)));
        sync();
      }, { signal: this.signal });
    }

    input.addEventListener('change', sync, { signal: this.signal });
    sync();
  }

  /* -------------------------------------------------------------- varyant */
  #initVariants() {
    const data = this.ref('variantData');
    if (!data) return;

    try { this.variants = JSON.parse(data.textContent ?? '[]'); }
    catch { return; }

    this.selected = [...qsa('.dn-variant', this)].map((group) => {
      const active = group.querySelector('[aria-pressed="true"]');
      return active instanceof HTMLElement ? active.dataset.value ?? '' : '';
    });

    for (const option of qsa('.dn-variant__opt', this)) {
      option.addEventListener('click', () => {
        const el = /** @type {HTMLElement} */ (option);
        const index = Number(el.dataset.optionIndex) - 1;
        this.selected[index] = el.dataset.value ?? '';

        // Aynı gruptaki diğer seçenekler bırakılır.
        const group = el.closest('.dn-variant');
        for (const sibling of qsa('.dn-variant__opt', /** @type {HTMLElement} */ (group))) {
          sibling.setAttribute('aria-pressed', String(sibling === el));
        }

        const label = group?.querySelector('[data-ref^="optionValue"]');
        if (label) label.textContent = el.dataset.value ?? '';

        this.#applyVariant();
      }, { signal: this.signal });
    }
  }

  #applyVariant() {
    const match = this.variants.find((v) =>
      v.options.every((value, i) => value === this.selected[i])
    );

    const add = /** @type {HTMLButtonElement | null} */ (this.ref('add'));
    const idField = /** @type {HTMLInputElement | null} */ (this.ref('variantId'));
    const price = this.ref('price');
    const compare = this.ref('compare');
    const stock = this.ref('stock');
    const barPrice = this.querySelector('.dn-buybar__price');

    if (!match) {
      if (add) { add.disabled = true; add.textContent = this.#t('unavailable'); }
      return;
    }

    if (idField) idField.value = String(match.id);
    if (price) price.textContent = formatMoney(match.price);
    const exVat = this.ref('priceExVat');
    if (exVat) {
      const oran = Number(this.dataset.kdv || 20);
      exVat.textContent = formatMoney(Math.floor((match.price * 100) / (100 + oran)));
    }
    if (barPrice) barPrice.textContent = formatMoney(match.price);

    if (compare) {
      const has = match.compare_at_price && match.compare_at_price > match.price;
      compare.textContent = has ? formatMoney(match.compare_at_price) : '';
      /** @type {HTMLElement} */ (compare).hidden = !has;
    }

    if (stock) {
      stock.textContent = match.available ? this.#t('inStock') : this.#t('soldOut');
      stock.classList.toggle('dn-stock--out', !match.available);
    }

    if (add) {
      add.disabled = !match.available;
      add.textContent = match.available ? this.#t('addToCart') : this.#t('soldOut');
    }

    // Adres çubuğu seçili varyantı yansıtsın: paylaşılan bağlantı doğru gelsin.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(match.id));
    window.history.replaceState({}, '', url);
  }

  /** @param {string} key */
  #t(key) {
    const map = {
      addToCart: 'Sepete ekle',
      soldOut: 'Tükendi',
      inStock: 'Stokta',
      unavailable: 'Bu seçim mevcut değil',
    };
    return map[key] ?? '';
  }

  /* ------------------------------------------------------- mobil çubuk */
  #initBuyBar() {
    const bar = this.ref('buybar');
    const form = this.ref('form');
    if (!bar || !form) return;

    // Çubuk yalnızca asıl buton ekrandan çıkınca görünür.
    const observer = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting),
      { rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(form);

    this.ref('buybarAdd')?.addEventListener('click', () => {
      /** @type {HTMLFormElement} */ (form).requestSubmit();
    }, { signal: this.signal });
  }

  /* --------------------------------------------------------- sepete ekle */
  #initAddToCart() {
    const form = /** @type {HTMLFormElement | null} */ (this.ref('form'));
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      // Sepet çekmecesi sayfada yoksa tarayıcı normal gönderime devam eder.
      if (!document.querySelector('dn-cart-drawer')) return;
      event.preventDefault();

      const add = /** @type {HTMLButtonElement | null} */ (this.ref('add'));
      add?.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(window.DenetTheme?.routes?.cartAdd ?? '/cart/add', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error(String(response.status));

        /* silent: hemen ardından sepet çekmecesi açılıyor, ayrıca bildirim
           çıkarmak aynı şeyi iki kez söylemek olurdu. */
        emit(EVENTS.cartUpdated, { silent: true });
        emit('denet:eklendi', { button: add });
        emit(EVENTS.drawerOpen, { drawer: 'cart' });
      } catch {
        form.submit();
      } finally {
        add?.removeAttribute('aria-busy');
      }
    }, { signal: this.signal });
  }
}

define('dn-product', DnProduct);
